import { KeyRound, Mail } from "lucide-react";
import { useState, type FormEvent } from "react";

import { errorMessageOf, isPasskeySupported, isUserCancellation } from "../lib/auth";
import { useToast } from "../lib/toast";
import { supabase } from "../lib/supabase";

/**
 * サインイン画面。
 *
 * **普段はパスキー。マジックリンクは端末を替えたときの入口**(→ ADR-0016)。
 *
 * マジックリンクは新しい端末にパスキーを生やすためのものであって、
 * 常用の入口ではない。そのため下段に置き、説明を添えている。
 *
 * `shouldCreateUser: false` を必ず付ける。付けないと、知らないアドレスを
 * 打ち込んだだけでアカウントが増えてしまう。
 */
export default function SignIn() {
  const toast = useToast();
  const [busy, setBusy] = useState<"passkey" | "link" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [showLink, setShowLink] = useState(false);

  const passkeyAvailable = isPasskeySupported();

  async function signInWithPasskey() {
    setBusy("passkey");
    setError(null);
    setNotice(null);
    try {
      const { error: signInError } = await supabase.auth.signInWithPasskey();
      if (signInError) throw signInError;
      // 成功すると認証状態の監視がアプリ本体へ切り替える。トーストは画面をまたいで残る
      toast("サインインしました。");
    } catch (e) {
      // 「キャンセルした」は失敗ではないので黙って戻す
      if (!isUserCancellation(e)) {
        console.error("サインインに失敗", e);
        toast("サインインに失敗しました。", "error");
      }
    } finally {
      setBusy(null);
    }
  }

  async function sendMagicLink(event: FormEvent) {
    event.preventDefault();
    setBusy("link");
    setError(null);
    setNotice(null);
    try {
      const { error: sendError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: window.location.origin,
        },
      });
      if (sendError) throw sendError;
      // 宛先が登録済みかどうかは伝えない。存在するアドレスを探る手掛かりになる
      setNotice("メールを送りました。10分以内にリンクを開いてください。");
    } catch (e) {
      setError(errorMessageOf(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <main className="mx-auto flex min-h-full max-w-sm flex-col justify-center gap-8 p-6">
      <header className="text-center">
        <h1 className="text-2xl font-semibold">Yuu&amp;mi</h1>
        <p className="mt-2 text-sm text-[var(--color-sub)]">二人だけのアルバム</p>
      </header>

      {/* 共有リンクから来たとき。サインインすると Shell 側が対象の詳細を開く */}
      {new URLSearchParams(window.location.search).has("media") && (
        <p className="text-center text-sm text-[var(--color-teal)]">サインインしてください。</p>
      )}

      {passkeyAvailable ? (
        <button
          type="button"
          onClick={() => void signInWithPasskey()}
          disabled={busy !== null}
          className="flex items-center justify-center gap-2 rounded-xl bg-[var(--color-teal)] px-4 py-3 font-medium text-white disabled:opacity-50"
        >
          <KeyRound className="size-5" aria-hidden />
          {busy === "passkey" ? "認証中..." : "パスキーでサインイン"}
        </button>
      ) : (
        <p className="rounded-xl border border-[var(--color-line)] p-4 text-sm text-[var(--color-sub)]">
          このブラウザはパスキーに対応していません。HTTPS でアクセスしているか確認してください。
        </p>
      )}

      {notice !== null && <p className="text-sm text-[var(--color-teal)]">{notice}</p>}
      {error !== null && (
        <p role="alert" className="text-sm text-[var(--color-plum)]">
          {error}
        </p>
      )}

      <div className="border-t border-[var(--color-line)] pt-6">
        {showLink ? (
          <form onSubmit={(e) => void sendMagicLink(e)} className="flex flex-col gap-3">
            <p className="text-xs text-[var(--color-sub)]">
              登録済みのメールアドレスにサインイン用のリンクを送ります。
              入った後、この端末にパスキーを登録してください。
            </p>
            <input
              type="email"
              required
              autoComplete="username"
              placeholder="メールアドレス"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
              className="rounded-lg border border-[var(--color-line)] bg-white px-3 py-2 text-base"
            />
            <button
              type="submit"
              disabled={busy !== null}
              className="flex items-center justify-center gap-2 rounded-lg border border-[var(--color-line)] px-4 py-2 text-sm disabled:opacity-50"
            >
              <Mail className="size-4" aria-hidden />
              {busy === "link" ? "送信中..." : "リンクを送る"}
            </button>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => {
              setShowLink(true);
            }}
            className="w-full text-center text-sm text-[var(--color-sub)] underline"
          >
            パスキーが使えないとき
          </button>
        )}
      </div>
    </main>
  );
}
